import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchLeadById, addFollowUp, editFollowUp,
  scheduleDemo, rescheduleDemo, updateDemoStatus,
} from "./adminLeadService";
import { useToast } from "../../common/toast/ToastContext";
import "../courses/adminCourses.css";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "—";

const toLabel = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const LEAD_SOURCE_LABELS = {
  ORGANIC: "Organic", REFERRAL: "Referral", WHATSAPP: "WhatsApp",
  INSTAGRAM: "Instagram", FACEBOOK: "Facebook", SOCIAL_MEDIA: "Social Media",
  PAID_AD: "Paid Ad", WALK_IN: "Walk In", EMAIL: "Email", WEBSITE: "Website", OTHER: "Other",
};

const LEAD_STATUS_BADGE = {
  NEW: "ac-badge-gray", CONTACTED: "ac-badge-blue", INTERESTED: "ac-badge-purple",
  DEMO_SCHEDULED: "ac-badge-orange", DEMO_DONE: "ac-badge-purple",
  CONVERTED: "ac-badge-green", NOT_INTERESTED: "ac-badge-red",
  NURTURING: "ac-badge-yellow", ENROLLED: "ac-badge-green", LOST: "ac-badge-red",
};

const LEAD_STATUSES = [
  "NEW", "CONTACTED", "INTERESTED", "DEMO_SCHEDULED", "DEMO_DONE",
  "CONVERTED", "NOT_INTERESTED", "NURTURING", "ENROLLED", "LOST",
];

const DEMO_STATUS_BADGE = {
  SCHEDULED: "ac-badge-blue", COMPLETED: "ac-badge-green",
  NO_SHOW: "ac-badge-orange", CANCELLED: "ac-badge-red",
};

const CONTACT_METHODS    = ["WHATSAPP", "EMAIL", "PHONE", "ZOOM", "IN_PERSON"];
const INTERACTION_TYPES  = ["OUTBOUND_CALL", "INBOUND_CALL", "MESSAGE", "DEMO", "MEETING"];
const STUDENT_RESPONSES  = ["INTERESTED", "NOT_INTERESTED", "THINKING", "NO_RESPONSE", "CALLBACK_REQUESTED"];
const OUTCOMES           = ["DEMO_BOOKED", "ENROLLED", "FOLLOW_UP_SCHEDULED", "LOST", "NURTURING", "NO_ACTION"];

const EMPTY_FOLLOWUP = {
  followupDate: new Date().toISOString().slice(0, 16),
  contactMethod: "WHATSAPP",
  interactionType: "OUTBOUND_CALL",
  conversationSummary: "",
  studentResponse: "",
  outcome: "",
  nextFollowupDate: "",
  nextFollowupAction: "",
};

/* ── Demo Modal ── */
const DemoModal = ({ demo, leadId, onClose, onDone }) => {
  const isReschedule = Boolean(demo?.id);
  const [scheduledAt,  setScheduledAt]  = useState(
    demo?.scheduledAt ? new Date(demo.scheduledAt).toISOString().slice(0, 16) : ""
  );
  const [durationMins, setDurationMins] = useState(demo?.durationMins ?? 30);
  const [notes,        setNotes]        = useState(demo?.notes ?? "");
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduledAt) { setError("Please select a date and time."); return; }
    setError(""); setSaving(true);
    try {
      const body = { scheduledAt: new Date(scheduledAt).toISOString(), durationMins: Number(durationMins), notes };
      isReschedule ? await rescheduleDemo(leadId, body) : await scheduleDemo(leadId, body);
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || "Failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 440, boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        <h3 style={{ margin: "0 0 1.25rem", fontSize: "1.1rem" }}>
          {isReschedule ? "Reschedule Demo" : "Schedule Demo"}
        </h3>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: "0.85rem" }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="ac-form-group">
            <label>Date &amp; Time *</label>
            <input className="ac-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} required />
          </div>
          <div className="ac-form-group">
            <label>Duration (mins)</label>
            <input className="ac-input" type="number" min="15" max="120" value={durationMins} onChange={(e) => setDurationMins(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
          <div className="ac-form-group">
            <label>Notes</label>
            <textarea className="ac-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>
              {saving ? "Saving…" : isReschedule ? "Reschedule" : "Schedule"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Demo Section ── */
const DemoSection = ({ leadId, demo, onRefresh }) => {
  const { showToast } = useToast();
  const [modal, setModal] = useState(false);

  const handleStatus = async (to) => {
    const labels = { COMPLETED: "mark as completed", NO_SHOW: "mark as no show", CANCELLED: "cancel this demo" };
    if (!window.confirm(`Are you sure you want to ${labels[to]}?`)) return;
    try {
      await updateDemoStatus(leadId, to);
      showToast(`Demo marked as ${toLabel(to)}.`, "success");
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update status.", "error");
    }
  };

  if (!demo) {
    return (
      <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 4 }}>Demo Session</div>
            <div style={{ color: "#9ca3af", fontSize: "0.88rem" }}>No demo session scheduled yet.</div>
          </div>
          <button className="ac-btn ac-btn-primary" onClick={() => setModal(true)}>Schedule Demo</button>
        </div>
        {modal && <DemoModal demo={null} leadId={leadId} onClose={() => setModal(false)} onDone={() => { setModal(false); showToast("Demo scheduled.", "success"); onRefresh(); }} />}
      </div>
    );
  }

  const isScheduled = demo.status === "SCHEDULED";

  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem" }}>Demo Session</div>
        <span className={`ac-badge ${DEMO_STATUS_BADGE[demo.status] || "ac-badge-gray"}`}>{toLabel(demo.status)}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
        {[["Scheduled At", fmtDateTime(demo.scheduledAt)], ["Duration", `${demo.durationMins} mins`]].map(([label, val]) => (
          <div key={label} style={{ background: "#f9fafb", borderRadius: 8, padding: "0.6rem 0.9rem" }}>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: 2 }}>{label}</div>
            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{val}</div>
          </div>
        ))}
      </div>

      {isScheduled && demo.zoomJoinUrl && (
        <div style={{ marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <a href={demo.zoomJoinUrl} target="_blank" rel="noreferrer" className="ac-btn ac-btn-primary ac-btn-sm">🎥 Join Zoom</a>
          {demo.zoomPassword && <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>Password: <strong>{demo.zoomPassword}</strong></span>}
        </div>
      )}

      {demo.notes && (
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "0.75rem", fontSize: "0.88rem", color: "#374151" }}>
          <span style={{ fontWeight: 600, marginRight: 6 }}>Notes:</span>{demo.notes}
        </div>
      )}

      {isScheduled && (
        <div className="ac-actions" style={{ marginTop: "0.5rem" }}>
          <button className="ac-btn ac-btn-warning ac-btn-sm" onClick={() => setModal(true)}>Reschedule</button>
          <button className="ac-btn ac-btn-success ac-btn-sm" onClick={() => handleStatus("COMPLETED")}>Mark Complete</button>
          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => handleStatus("NO_SHOW")}>No Show</button>
          <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => handleStatus("CANCELLED")}>Cancel Demo</button>
        </div>
      )}

      {modal && <DemoModal demo={demo} leadId={leadId} onClose={() => setModal(false)} onDone={() => { setModal(false); showToast("Demo rescheduled.", "success"); onRefresh(); }} />}
    </div>
  );
};

/* ── Follow-up Form ── */
const FollowUpForm = ({ leadId, existing, onDone, onCancel }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(
    existing
      ? {
          followupDate:        existing.followupDate ? new Date(existing.followupDate).toISOString().slice(0, 16) : "",
          contactMethod:       existing.contactMethod       || "WHATSAPP",
          interactionType:     existing.interactionType     || "OUTBOUND_CALL",
          conversationSummary: existing.conversationSummary || "",
          studentResponse:     existing.studentResponse     || "",
          outcome:             existing.outcome             || "",
          nextFollowupDate:    existing.nextFollowupDate    || "",
          nextFollowupAction:  existing.nextFollowupAction  || "",
        }
      : { ...EMPTY_FOLLOWUP }
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        followupDate:     new Date(form.followupDate).toISOString(),
        nextFollowupDate: form.nextFollowupDate || null,
        studentResponse:  form.studentResponse  || null,
        outcome:          form.outcome          || null,
        conversationSummary: form.conversationSummary || null,
        nextFollowupAction:  form.nextFollowupAction  || null,
      };
      if (existing) {
        await editFollowUp(leadId, existing.id, payload);
        showToast("Follow-up updated.", "success");
      } else {
        await addFollowUp(leadId, payload);
        showToast("Follow-up added.", "success");
      }
      onDone();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save follow-up.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: "#f9fafb", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
      <div className="ac-form-row">
        <div className="ac-form-group">
          <label>Follow-up Date &amp; Time *</label>
          <input className="ac-input" type="datetime-local" value={form.followupDate} onChange={(e) => set("followupDate", e.target.value)} required />
        </div>
        <div className="ac-form-group">
          <label>Contact Method *</label>
          <select className="ac-input" value={form.contactMethod} onChange={(e) => set("contactMethod", e.target.value)} required>
            {CONTACT_METHODS.map((m) => <option key={m} value={m}>{toLabel(m)}</option>)}
          </select>
        </div>
      </div>

      <div className="ac-form-row">
        <div className="ac-form-group">
          <label>Interaction Type *</label>
          <select className="ac-input" value={form.interactionType} onChange={(e) => set("interactionType", e.target.value)} required>
            {INTERACTION_TYPES.map((t) => <option key={t} value={t}>{toLabel(t)}</option>)}
          </select>
        </div>
        <div className="ac-form-group">
          <label>Student Response</label>
          <select className="ac-input" value={form.studentResponse} onChange={(e) => set("studentResponse", e.target.value)}>
            <option value="">— select —</option>
            {STUDENT_RESPONSES.map((r) => <option key={r} value={r}>{toLabel(r)}</option>)}
          </select>
        </div>
      </div>

      <div className="ac-form-group">
        <label>Conversation Summary</label>
        <textarea className="ac-input" value={form.conversationSummary} onChange={(e) => set("conversationSummary", e.target.value)} rows={2} />
      </div>

      <div className="ac-form-row">
        <div className="ac-form-group">
          <label>Outcome</label>
          <select className="ac-input" value={form.outcome} onChange={(e) => set("outcome", e.target.value)}>
            <option value="">— select —</option>
            {OUTCOMES.map((o) => <option key={o} value={o}>{toLabel(o)}</option>)}
          </select>
        </div>
        <div className="ac-form-group">
          <label>Next Follow-up Date</label>
          <input className="ac-input" type="date" value={form.nextFollowupDate} onChange={(e) => set("nextFollowupDate", e.target.value)} />
        </div>
      </div>

      <div className="ac-form-group">
        <label>Next Follow-up Action</label>
        <input className="ac-input" value={form.nextFollowupAction} onChange={(e) => set("nextFollowupAction", e.target.value)} placeholder="e.g. Check if they joined the demo" />
      </div>

      <div className="ac-form-actions">
        <button type="submit" className="ac-btn ac-btn-primary ac-btn-sm" disabled={saving}>
          {saving ? "Saving…" : existing ? "Update" : "Add"}
        </button>
        <button type="button" className="ac-btn ac-btn-ghost ac-btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

/* ── Follow-up Timeline ── */
const FollowUpSection = ({ leadId, followUps, onRefresh }) => {
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null); // followUp object being edited

  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem" }}>
          Follow-up Timeline
          {followUps?.length > 0 && (
            <span style={{ marginLeft: 8, background: "#dbeafe", color: "#1d4ed8", borderRadius: 999, padding: "0.1rem 0.5rem", fontSize: "0.75rem" }}>
              {followUps.length}
            </span>
          )}
        </div>
        {!open && !editing && (
          <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => setOpen(true)}>
            + Add Follow-up
          </button>
        )}
      </div>

      {open && (
        <FollowUpForm
          leadId={leadId}
          existing={null}
          onDone={() => { setOpen(false); onRefresh(); }}
          onCancel={() => setOpen(false)}
        />
      )}

      {(!followUps || followUps.length === 0) && !open && (
        <div className="ac-empty" style={{ padding: "1.5rem" }}>No follow-ups yet.</div>
      )}

      {followUps && followUps.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {followUps.map((f, i) => (
            <div key={f.id ?? i}>
              {editing?.id === f.id ? (
                <FollowUpForm
                  leadId={leadId}
                  existing={f}
                  onDone={() => { setEditing(null); onRefresh(); }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div style={{ borderLeft: "3px solid #2563eb", paddingLeft: "0.9rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginBottom: 2 }}>
                      {fmtDateTime(f.followupDate)} · <strong>{toLabel(f.contactMethod)}</strong> · {toLabel(f.interactionType)}
                    </div>
                    <button className="ac-btn ac-btn-ghost ac-btn-sm" style={{ fontSize: "0.72rem", padding: "0.15rem 0.5rem" }} onClick={() => setEditing(f)}>
                      Edit
                    </button>
                  </div>

                  {f.conversationSummary && (
                    <div style={{ fontSize: "0.88rem", color: "#374151", marginBottom: 4 }}>{f.conversationSummary}</div>
                  )}

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {f.studentResponse && (
                      <span className="ac-badge ac-badge-blue" style={{ fontSize: "0.7rem" }}>{toLabel(f.studentResponse)}</span>
                    )}
                    {f.outcome && (
                      <span className="ac-badge ac-badge-green" style={{ fontSize: "0.7rem" }}>{toLabel(f.outcome)}</span>
                    )}
                  </div>

                  {(f.nextFollowupDate || f.nextFollowupAction) && (
                    <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 4 }}>
                      {f.nextFollowupDate && <>Next: {fmtDate(f.nextFollowupDate)}</>}
                      {f.nextFollowupAction && <> — {f.nextFollowupAction}</>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Page ── */
const AdminLeadDetailPage = () => {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const [lead, setLead]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchLeadById(id);
      setLead(res.data);
    } catch {
      setError("Failed to load lead.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="ac-loading">Loading…</div>;
  if (error)   return <div style={{ color: "#dc2626", padding: "1rem" }}>{error}</div>;
  if (!lead)   return <div className="ac-empty">Lead not found.</div>;

  return (
    <div>
      <div className="ac-header">
        <div>
          <h2>{lead.name}</h2>
          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <span className={`ac-badge ${LEAD_STATUS_BADGE[lead.currentStatus] || "ac-badge-gray"}`}>{toLabel(lead.currentStatus)}</span>
            <span className="ac-badge ac-badge-gray">{LEAD_SOURCE_LABELS[lead.leadSource] || toLabel(lead.leadSource)}</span>
          </div>
        </div>
        <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => navigate("/admin/leads")}>← Leads</button>
      </div>

      {/* ── Section A: Lead Info ── */}
      <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: "1.5rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "1rem" }}>Lead Info</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.75rem" }}>
          {[
            ["Email",          lead.email          || "—"],
            ["WhatsApp",       lead.whatsappNumber  || "—"],
            ["English Level",  lead.englishLevel    || "—"],
            ["Enquiry Date",   fmtDate(lead.enquiryDate)],
            ["Registered User", lead.registeredUserEmail || "—"],
          ].map(([label, val]) => (
            <div key={label} style={{ background: "#f9fafb", borderRadius: 8, padding: "0.6rem 0.9rem" }}>
              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: 2 }}>{label}</div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{val}</div>
            </div>
          ))}
        </div>
        {lead.learningGoal && (
          <div style={{ marginTop: "0.75rem", fontSize: "0.88rem", color: "#374151" }}>
            <span style={{ fontWeight: 600 }}>Learning Goal: </span>
            <span style={{ whiteSpace: "pre-wrap" }}>{lead.learningGoal}</span>
          </div>
        )}
        {lead.notes && (
          <div style={{ marginTop: "0.5rem", fontSize: "0.88rem", color: "#374151" }}>
            <span style={{ fontWeight: 600 }}>Notes: </span>
            <span style={{ whiteSpace: "pre-wrap" }}>{lead.notes}</span>
          </div>
        )}
      </div>

      {/* ── Section B: Demo Session ── */}
      <DemoSection leadId={id} demo={lead.demoSession} onRefresh={load} />

      {/* ── Section C: Follow-ups ── */}
      <FollowUpSection leadId={id} followUps={lead.followUps} onRefresh={load} />
    </div>
  );
};

export default AdminLeadDetailPage;
